import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { DataTableComponent } from '../../../../../../../../shared/components/data-table/data-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { BehaviorSubject } from 'rxjs';
import { DataTableColumn } from '../../../../../../../../shared/components/data-table/DataTableColumn.model';
import { DataTableFilter } from '../../../../../../../../shared/components/data-table/filters/components/DataTableFilter';
import { PropertiesFilterStatusComponent } from '../../../../../../../properties/properties-table/components/filters/properties-filter-status/properties-filter-status.component';
import { PropertyTableRow } from '../../../../../../../properties/properties-table/models/PropertyTableRow.model';
import { Logger } from '../../../../../../../../infrastructure/logging/logger';
import { SearchService } from '../../../../../../../../shared/search/search.service';
import { toTitleCase } from '../../../../../../../../utilities/StringUtility';
import { FilterParams, SortParams } from '../../../../../../../../shared/search/SearchParams';
import { ColorProp, getColor } from '../../../../../../../../layout/Colors';
import { PropertyApiService } from '../../../../../../../properties/services/property.api.service';

@Component({
  selector: 'app-property-select-dialog-table',
  imports: [
    CommonModule,
    DataTableComponent,
    MatCard,
    MatButtonModule,
    MatDialogModule
  ],
  templateUrl: './property-select-dialog-table.component.html',
  styleUrl: './property-select-dialog-table.component.scss'
})
export class PropertySelectDialogTableComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  @Input('organizationId') organizationId!: any;
  @Input() rowClicked?: (row: any) => void;
  @Input() onActionButtonClick?: (row: any) => void;

  @Output() selectedProperty = new EventEmitter<any>();

  STATUS_ACTIVE = 1;
  STATUS_INACTIVE = 2;

  filterDefinitions: DataTableFilter[] = [
    { id: 'property-filter-status', name: 'Property Status', shortName: 'Status', component: PropertiesFilterStatusComponent }
  ];

  displayedColumns: DataTableColumn[] = [
    { column: 'id', display: `ID` },
    { column: 'referenceId', display: 'Property ID' },
    { column: 'name', display: 'Property Name' },
    { column: 'city', display: 'City/Town' },
    { column: 'state', display: 'State/Province' },
    { column: 'postalCode', display: 'Postal Code' },
    { column: 'pillStatus', display: 'Status' },
    { column: 'reservationFormSelectPropertyButton', display: '' }
  ];

  tableToApiColumnMap: { [key: string]: string } = {
    'id': 'propertyId',
    'referenceId': 'referenceId',
    'name': 'name',
    'city': 'city',
    'state': 'state',
    'postalCode': 'postalCode',
    'status': 'pillStatus'
  }

  apiToTableColumnMap: { [key: string]: string } = {
    'propertyId': 'id',
    'referenceId': 'referenceId',
    'name': 'name',
    'city': 'city',
    'state': 'state',
    'postalCode': 'postalCode',
    'status': 'pillStatus'
  }

  dataSource = new MatTableDataSource<PropertyTableRow>();
  initialLoad = true;
  loading = new BehaviorSubject<boolean>(false);
  tableLength: number = 0;
  pageSize: number = 10;
  pageNumber: number = 1;
  keyword = '';

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    public propertiesService: PropertyApiService,
    private queryService: SearchService,
    private logService: Logger,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.resetQueryServiceState();
    this.loadPage();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.keyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  private resetQueryServiceState() {
    this.queryService.resetQuery(10);
    this.queryService.setSort("propertyId", -1);
    this.queryService.setOrganizationId(this.organizationId);
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      const filter: FilterParams = {
        fieldName: 'status',
        value: ["ACTIVE"],
        range: false
      };
      this.queryService.replaceFilter(filter);

      const { properties, totalRecords } = await this.propertiesService.searchProperties(this.queryService.currentQuery, true);
      const mappedProperties = properties.map(p => this.buildPropertyTableRow(p));
      this.updateTableDataSource(mappedProperties, totalRecords);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Properties', 'Error while retrieving Properties');
    } finally {
      this.loading.next(false);
    }
  }

  buildPropertyTableRow(property: any): PropertyTableRow {
    return {
      id: property.propertyId,
      referenceId: property.referenceId,
      name: property.name,
      city: property.city,
      state: property.state,
      postalCode: property.postalCode,
      pillStatus: toTitleCase(property.status),
      action: this.onActionButtonClick != null
    }
  }

  updateTableDataSource(properties: PropertyTableRow[], totalRecords: number) {
    this.dataSource = new MatTableDataSource(properties);
    this.tableLength = totalRecords;
  }

  onSelectPropertyClick(row: any) {
    this.selectedProperty.emit(row);
  }

  onPageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.queryService.setPageSize(event.pageSize);
    this.queryService.setPage(event.pageIndex + 1);
    this.loadPage();
  }

  onSearchEvent(keyword: string) {
    this.addKeywordToQuery(keyword);
    this.loadPage();
  }

  private addKeywordToQuery(keyword: string) {
    this.keyword = keyword;
    this.queryService.addTextQuery(keyword);
    this.queryService.setPage(1);
  }

  onSortEvent(sortParams: SortParams) {
    this.addSortToQuery(sortParams);
    this.loadPage();
  }

  private addSortToQuery(sortParams: SortParams) {
    sortParams.sortField = this.tableToApiColumnMap[sortParams.sortField];
    this.queryService.addSort(sortParams);
  }

  getPropertyStatusColor(status: string): string {
    if(status?.toLowerCase() === 'active') {
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }
}